import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { XsI18nPipe } from '@nx-fe/pipes';
import { AuthenticationService, GlobalEventBusService, TicketContextService, TicketService } from '@nx-fe/services';
import { Subscription } from 'rxjs';
import { ActivityContextService } from 'xsmodules/ticket/components/ticket-details-panel/activity/activity-context';
import { GlobalEvents } from '../../../../../../../core/global-event-constants';
import { Activity, NoteActivity } from '../../../../../ticket.model';

@Component({
  selector: 'xs-note',
  templateUrl: './note.component.html',
  styleUrls: ['./note.component.scss'],
})
export class NoteComponent implements OnInit, OnDestroy {
  @Input() noteActivity: NoteActivity;
  @Input() placeholder: string;
  @Input() error: string;
  @Input('disable') isSaving = false;
  @Input() activityCtrl: ActivityContextService;
  @Output() updatePrivacy = new EventEmitter<boolean>();
  @Output() saveNote = new EventEmitter<NoteActivity>();
  @Output() updateEditing = new EventEmitter<boolean>();

  @ViewChild('noteInstance') noteComponent: ElementRef;

  isPrivate: boolean;
  noteText = '';
  editing = false;
  toggleFocus = false;
  noteEdit = true;
  needsFocusForResolution = false;

  private resolutionNoteRequiredSub: Subscription;
  constructor(
    private xsI18nPipe: XsI18nPipe,
    private authenticationService: AuthenticationService,
    private globalEventBusService: GlobalEventBusService,
    private ticketService: TicketService,
    private ticketContextService: TicketContextService
  ) {}

  ngOnInit(): void {
    const hasPrivatePermission = this.ticketContextService.canAddPrivateNote();
    const hasPublicPermission = this.ticketContextService.canAddPublicNote();
    if ((this.isNew || this.isNewResolutionNote) && hasPrivatePermission) {
      this.noteActivity.details.isPrivate = true;
      this.updatePrivacy.emit(true);
    }

    this.noteText = this.noteActivity?.description || '';
    this.isPrivate = this.noteActivity.details.isPrivate;

    this.resolutionNoteRequiredSub = this.globalEventBusService.register(GlobalEvents.resolutionNoteRequired, () => {
      this.verifyResolutionNote();
    });

    if (this.isNew) {
      this.setEditing(true);
    }
    if (!this.placeholder) {
      let placeholderKey: string;
      // this.placeholder = this.xsI18nPipe.transform('note_Placeholder_Text');
      if (!this.noteActivity.details.isResolution) {
        placeholderKey = 'note_Placeholder_Text';
      } else {
        placeholderKey = 'note_ResolutionNotePlaceholder_Text';
      }
      this.placeholder = this.xsI18nPipe.transform(placeholderKey);
    }

    if (!!this.noteActivity.activityId) {
      this.ticketService
        .getActivity(parseInt(this.ticketContextService.ticket.ticketId, 10), this.noteActivity.activityId)
        .subscribe(result => {
          const activity = (<unknown>result) as Activity;
          if (activity.creationUserId.toLowerCase() === this.authenticationService.getUserId().toLowerCase()) {
            this.noteEdit = true;
          }
          if (this.ticketContextService.canAddResolutionNote() || this.ticketContextService.canAddNote()) {
            this.noteEdit = this.noteActivity.details.isPrivate ? hasPrivatePermission : hasPublicPermission;
          } else if (!this.ticketContextService.isDraftTicket() && !this.isNew) {
            // At this point do not have edit permission
            this.noteEdit = false;
          }
        });
    }
  }

  get isDirty(): boolean {
    return this.noteText !== this.noteActivity?.description;
  }

  ngAfterViewChecked(): void {
    if (this.needsFocusForResolution)  {
      this.noteComponent.nativeElement.focus();
      this.needsFocusForResolution = false;
    }
  }

  ngOnDestroy(): void {
    if (this.resolutionNoteRequiredSub) {
      this.resolutionNoteRequiredSub.unsubscribe();
    }
  }

  verifyResolutionNote(): void {
    if (this.isEmpty() && this.noteActivity.details.isResolution) {
      this.setError(this.xsI18nPipe.transform('note_Error', this.xsI18nPipe.transform('note_Resolution_Required')));
      this.needsFocusForResolution = true;
    }
  }

  get isNew(): boolean {
    return this.noteActivity && !this.noteActivity.activityId;
  }

  get needsFocus(): boolean   {
    return this.isNew || this.needsFocusForResolution;
  }

  get isNewResolutionNote(): boolean {
    return this.noteActivity && !this.noteActivity.description && this.noteActivity.details?.isResolution;
  }

  /**
   * Indicates whether the footer should be visible at all in the note
   */
  get isFooterVisible(): boolean {
    return this.isFooterButtonsVisible || this.isPrivacySwitchVisible;
  }

  /** Indicates whether the footer buttons and error messages should be shown or not */
  get isFooterButtonsVisible(): boolean {
    const ret = this.hasChanged || this.isNew;
    return ret;
  }

  /** Indicate whether description or privacy flag has changed */
  get hasChanged(): boolean {
    const descriptionChanged = (this.noteText || '') !== (this.noteActivity?.description || '');
    const privacyFlagChanged =
      this.ticketContextService.canAddNote() &&
      this.noteActivity?.details?.isPrivate !== undefined &&
      !!this.isPrivate !== this.noteActivity.details.isPrivate;
    return descriptionChanged || privacyFlagChanged;
  }

  save() {
    const temp = this.noteActivity;
    temp.description = this.noteText;
    temp.details.isPrivate = this.isPrivate;
    this.saveNote.emit(temp);
    this.setError(undefined);
    const canEdit = this.isPrivate
      ? this.ticketContextService.canAddPrivateNote()
      : this.ticketContextService.canAddPublicNote();
    if (!canEdit) {
      this.noteEdit = false;
    }
  }

  cancel() {
    if (this.isNew) {
      this.activityCtrl.delete();
    } else {
      // This was an update operation... abort it and reset the text
      this.noteText = this.noteActivity.description;
      this.isPrivate = this.noteActivity.details.isPrivate;
      this.setError(undefined);
    }
  }

  public get shouldEnableToggle(): boolean {
    if (this.isPrivate) {
      // is switch to public allowed?
      return this.ticketContextService.canAddPublicNote();
    } else {
      // is switch to private allowed?
      return this.ticketContextService.canAddPrivateNote();
    }
  }

  /** Determines whether the note is empty */
  private isEmpty(): boolean {
    return !this.noteText || this.noteText.trim().length === 0;
  }

  private setError(err: string) {
    this.error = err;
  }

  setEditing(value: boolean) {
    this.editing = value;
    this.updateEditing.emit(value);
  }

  /** Indicates whether the privacy switch buttons should be shown or not
   *
   * @return whether the privacy switch should be shown on the page or not
   */
  get isPrivacySwitchVisible(): boolean {
    return (
      // if there is no noteActivity, this is a note inside the attachment avtivity
      this.noteActivity &&
      (this.editing || this.isFooterButtonsVisible) &&
      this.shouldEnableToggle &&
      (!this.noteActivity.details.isPrivate || this.isNew || this.noteActivity.details.isResolution)
    );
  }

  get minTextAreaRows(): number {
    if (this.noteActivity && (this.hasChanged || this.editing || this.isNew)) {
      return 3;
    }
    return 1;
  }

  updatePrivateStatus(event: MouseEvent): void {
    this.toggleFocus = !this.toggleFocus;
    event.preventDefault();
  }

  togglePrivacy(): void {
    this.isPrivate = !this.isPrivate;
    this.updatePrivacy.emit(this.isPrivate);
  }
}
